import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { WindowEnums } from 'src/app/enum/window-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { FilterGlobalResponseModel } from 'src/app/models/responses/filter-global-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { TransportInvoiceDetailTableModel } from 'src/app/models/tables/additional/transport-invoice-detail-table.model';
import { TransportInvoiceTableModel } from 'src/app/models/tables/additional/transport-invoice-table.model';
import { TransportInvoiceDetailService } from 'src/app/services/additional/transport-invoice-detail.service';
import { TransportInvoiceService } from 'src/app/services/additional/transport-invoice.service';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-transport-invoice',
  templateUrl: './transport-invoice.component.html',
  styleUrls: ['./transport-invoice.component.scss'],
  standalone: false
})
export class TransportInvoiceComponent implements OnInit {
  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  data: PagedResponseModel<TransportInvoiceTableModel> = new PagedResponseModel<TransportInvoiceTableModel>();
  detailData: PagedResponseModel<TransportInvoiceDetailTableModel> = new PagedResponseModel<TransportInvoiceDetailTableModel>();
  header: ColumnResponseModel[] = [];
  detailHeader: FilterGlobalResponseModel = new FilterGlobalResponseModel();
  listStoreConsolidated: ColumnResponseModel = new ColumnResponseModel();
  listDifferences: ColumnResponseModel = new ColumnResponseModel();
  differences: number = 1;
  filters: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Facturas de Transporte';
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  url: string = 'transport-invoice';
  userAuthenticated: UserModel = new UserModel();

  constructor(
    private confirmDialogService: ConfirmDialogService,
    private headerService: HeaderService,
    private optionWindowService: OptionWindowService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private transportInvoiceService: TransportInvoiceService,
    private transportInvoiceDetailService: TransportInvoiceDetailService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService
  ) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllTransportInvoicesWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob: Blob = await firstValueFrom(
        this.transportInvoiceService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant)
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'FacturasTransporte.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar las facturas de transporte.', error);
    }
  }

  async getAllTransportInvoicesWithPagination() {
    try {
      this.data = await firstValueFrom(
        this.transportInvoiceService.getAllTransportInvoicesWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
    } catch (error) {
      console.error('Error al buscar las facturas de transporte.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.header.length === 0) {
        this.header = await firstValueFrom(this.transportInvoiceService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general de facturas de transporte.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.columns.length === 0) {
        this.detailHeader = await firstValueFrom(this.transportInvoiceDetailService.getColumnsForDetailConsultationAsync());
      }
      if (this.detailHeader.filtersGlobal && this.detailHeader.filtersGlobal.length > 0) {
        this.listDifferences = this.detailHeader.filtersGlobal[0];
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de detalle.', error);
    }
  }

  async exportDetailConsultation() {
    try {
      const blob: Blob = await firstValueFrom(
        this.transportInvoiceDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant, this.differences)
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'FacturasTransporteDetalle.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar los detalles de facturas de transporte.', error);
    }
  }

  async getTransportInvoicesDetailsForDetailConsultation() {
    try {
      this.detailData = await firstValueFrom(
        this.transportInvoiceDetailService.getTransportInvoicesDetailsForDetailConsultation(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters, this.differences
        )
      );
    } catch (error) {
      console.error('Error al buscar los detalles de facturas de transporte.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'warning',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getPermissionsForUserByWindow(),
      this.getPermissionProfileByUrl(),
      this.getUserAuthenticated()
    ]);
  }

  async getPermissionsForUserByWindow() {
    try {
      this.permissionsUser = await firstValueFrom(
        this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.TransportInvoice)
      );
    } catch (error) {
      console.error('Error al cargar los permisos del usuario.', error);
    }
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getUserAuthenticated() {
    try {
      this.userAuthenticated = await firstValueFrom(
        this.userAuthenticatedService.userObservable.pipe(
          filter((user: UserModel) => !!user && user.userId !== 0)
        )
      );
    } catch (error) {
      console.error('Error al obtener el usuario autenticado.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.header.length = this.header.length === 1 ? 0 : this.header.length;
    this.detailHeader.columns.length = this.detailHeader.columns.length === 1 ? 0 : this.detailHeader.columns.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllTransportInvoicesWithPagination()
        ]);
        this.data = {
          ...this.data,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getTransportInvoicesDetailsForDetailConsultation()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  newRegister() {
    this.router.navigate([`/${this.url}/register`]);
  }

  onAction(event: { action: string, item?: TransportInvoiceTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.transportInvoiceId}`]);
    }
  }

  onDifferencesChange(value: any) {
    this.differences = Number(value);
    this.getTransportInvoicesDetailsForDetailConsultation();
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllTransportInvoicesWithPagination();
        break;
      case 1:
        await this.getTransportInvoicesDetailsForDetailConsultation();
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async reloadList(reload: boolean) {
    if (reload) {
      switch (this.activeTabIndex) {
        case 0:
          await this.getAllTransportInvoicesWithPagination();
          break;
        case 1:
          await this.getTransportInvoicesDetailsForDetailConsultation();
          break;
      }
    }
  }
}
